import { PageError } from "@/PageError";
import useLayoutData from "@/shared/hooks/useLayoutData";
import useRoleHooks from "@/shared/hooks/useRoleHooks";
import { reportAccessDenied } from "@/shared/atoms/useAccessDenied";
import { useEffect } from "react";
import { Redirect, Route, useHistory } from "react-router-dom";
import useAuthHook from "../../shared/hooks/useAuthHook";
import { ErrorBoundary } from "../ErrorBoundary";
import MyErrorBoundary from "../MyErrorBoundary";

export interface AuthRouteProps {
  children: React.ReactNode;
  hasAuthority?: boolean;
  exact: boolean;
  path: string;
}

const AuthRoutes = ({
  children,
  hasAuthority = true,
  ...rest
}: AuthRouteProps) => {
  const { isAuth } = useAuthHook();
  const { getRoles } = useRoleHooks();
  const history = useHistory();
  const { startPage } = useLayoutData();

  // Roles fetch is async; hasAuthority() is false until data exists — wait before denying
  const rolesReady = !getRoles.isPending;
  const shouldDeny = isAuth && !hasAuthority && rolesReady;

  useEffect(() => {
    window.focus();
  }, [history.location.pathname]);

  useEffect(() => {
    if (!shouldDeny) return;
    reportAccessDenied({ kind: "page", page: history.location.pathname });
    if (startPage) {
      history.push(startPage);
    }
  }, [shouldDeny, startPage, history]);

  return (
    <Route
      {...rest}
      render={() => {
        if (!isAuth) {
          return <Redirect to="/login" />;
        }

        if (!hasAuthority) {
          return null;
        }

        return (
          <ErrorBoundary fallback={(error) => <PageError error={error} />}>
            <MyErrorBoundary>{children}</MyErrorBoundary>
          </ErrorBoundary>
        );
      }}
    />
  );
};

export default AuthRoutes;
